import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser, verifyOtp, cancelTwoFactor } from "../../store/slices/authSlice";
import Icon from "../../components/Common/Icon";
import LegalDocumentModal from "../../components/Common/LegalDocumentModal";
import CookieConsentBanner from "../../components/Common/CookieConsentBanner";

const OTP_SECONDS = 120; // kod geçerlilik süresi (2 dakika)

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, pendingTwoFactor } = useSelector((state) => state.auth);

  // Lock State detection
  const lockedEmail = localStorage.getItem("lockedUserEmail");
  const lockedName = localStorage.getItem("lockedUserName");

  const [isLocked, setIsLocked] = useState(!!lockedEmail);
  const [email, setEmail] = useState(localStorage.getItem("rememberedEmail") || "");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem("rememberedEmail"));
  const [showPassword, setShowPassword] = useState(false);

  // 2FA
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(OTP_SECONDS);

  // Legal Modal State
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState("terms");

  const openLegal = (tab = "terms") => {
    setLegalModalTab(tab);
    setLegalModalOpen(true);
  };

  // Geçerli oturumda 2FA ekranına geçince geri sayımı başlat/sıfırla.
  useEffect(() => {
    if (!pendingTwoFactor) return;
    setCode("");
    setSecondsLeft(OTP_SECONDS);
    const id = setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [pendingTwoFactor]);

  const finishLogin = (user) => {
    if (!isLocked) {
      if (rememberMe) localStorage.setItem("rememberedEmail", email);
      else localStorage.removeItem("rememberedEmail");
    }
    localStorage.removeItem("lockedUserEmail");
    localStorage.removeItem("lockedUserName");
    navigate(user?.mustChangePassword ? "/change-password" : "/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loginEmail = isLocked ? lockedEmail : email;
    const result = await dispatch(loginUser({ email: loginEmail, password }));

    if (loginUser.fulfilled.match(result)) {
      // 2FA gerekiyorsa pendingTwoFactor state'i UI'ı kod ekranına geçirir.
      if (!result.payload.twoFactorRequired) {
        finishLogin(result.payload.auth?.user);
      }
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const result = await dispatch(verifyOtp({ email: pendingTwoFactor.email, code }));
    if (verifyOtp.fulfilled.match(result)) {
      finishLogin(result.payload.user);
    }
  };

  const handleResend = () => {
    const loginEmail = isLocked ? lockedEmail : email;
    dispatch(loginUser({ email: loginEmail, password }));
  };

  const handleSwitchAccount = () => {
    localStorage.removeItem("lockedUserEmail");
    localStorage.removeItem("lockedUserName");
    setIsLocked(false);
    setPassword("");
  };

  const mmss = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(secondsLeft % 60).padStart(2, "0")}`;
  const expired = secondsLeft === 0;

  // ----------------------------------------------------------- 2FA kod ekranı
  if (pendingTwoFactor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-bg-subtle to-secondary/10 p-4">
        <form
          onSubmit={handleVerify}
          className="w-full max-w-md bg-surface-card rounded-3xl shadow-modal p-8 space-y-6 border border-outline-variant"
        >
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
              <Icon name="sms" className="!text-[30px]" filled />
            </div>
            <h1 className="text-2xl font-bold text-on-surface">Doğrulama Kodu</h1>
            <p className="text-on-surface-variant text-sm mt-1">
              {pendingTwoFactor.maskedPhone} numaralı telefona gönderilen 6 haneli kodu girin.
            </p>
          </div>

          {error && (
            <div className="text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3 flex items-center gap-2">
              <Icon name="error" className="!text-[18px] flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <input
            type="text"
            inputMode="numeric"
            autoFocus
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-full text-center tracking-[0.6em] text-2xl font-bold border border-outline-variant rounded-xl px-3.5 py-3 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition bg-white"
            placeholder="______"
          />

          <div className="text-center text-sm">
            {expired ? (
              <span className="text-error font-medium">Kodun süresi doldu.</span>
            ) : (
              <span className="text-on-surface-variant">
                Kalan süre: <b className="text-on-surface">{mmss}</b>
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== 6 || expired}
            className="w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2 shadow-md shadow-primary/20"
          >
            {isLoading ? "Doğrulanıyor..." : (<><Icon name="check_circle" /> Doğrula ve Giriş Yap</>)}
          </button>

          <div className="flex items-center justify-between text-xs font-semibold">
            <button
              type="button"
              onClick={() => dispatch(cancelTwoFactor())}
              className="text-on-surface-variant hover:text-primary transition"
            >
              ← Geri
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={isLoading || !expired}
              className="text-primary hover:underline transition disabled:opacity-40 disabled:no-underline"
            >
              Kodu tekrar gönder
            </button>
          </div>
        </form>
      </div>
    );
  }

  // -------------------------------------------------------------- Giriş ekranı
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-bg-subtle to-secondary/10 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-surface-card rounded-3xl shadow-modal p-8 space-y-6 border border-outline-variant transition-all"
      >
        {isLocked ? (
          // Lock Screen UI
          <div className="text-center space-y-4">
            <div className="relative inline-block">
              <div className="w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center text-3xl font-extrabold mx-auto border-2 border-primary/20">
                {(lockedName ?? lockedEmail ?? "?").charAt(0).toUpperCase()}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-primary text-white rounded-full p-1.5 shadow-md flex items-center justify-center border border-white">
                <Icon name="lock" className="!text-[14px]" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-on-surface">Kilitli Ekran</h1>
              <p className="text-sm text-on-surface-variant mt-1">
                Tekrar giriş yapmak için şifrenizi girin.
              </p>
              <p className="text-xs font-semibold text-primary mt-1.5">{lockedEmail}</p>
            </div>
          </div>
        ) : (
          // Regular Login Header
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
              <Icon name="spa" className="!text-[32px]" filled />
            </div>
            <h1 className="text-2xl font-bold text-on-surface">Giriş Yap</h1>
            <p className="text-on-surface-variant text-sm mt-1">
              Hesabınıza erişmek için bilgilerinizi girin.
            </p>
          </div>
        )}

        {error && (
          <div className="text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3 flex items-center gap-2">
            <Icon name="error" className="!text-[18px] flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-4">
          {!isLocked && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1.5 text-on-surface-variant">
                E-posta
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition bg-white"
                placeholder="ornek@mail.com"
              />
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1.5 text-on-surface-variant">
              Şifre
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-outline-variant rounded-xl pl-3.5 pr-11 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition bg-white"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-3 text-on-surface-variant hover:text-primary transition"
              >
                <Icon name={showPassword ? "visibility_off" : "visibility"} className="!text-[20px]" />
              </button>
            </div>
          </div>

          {!isLocked && (
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-on-surface-variant">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded text-primary border-outline-variant focus:ring-primary w-4 h-4"
                />
                Beni Hatırla
              </label>
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm font-medium text-primary hover:underline"
              >
                Şifremi unuttum?
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3 pt-2">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2 shadow-md shadow-primary/20"
          >
            {isLoading ? "Yükleniyor..." : (<><Icon name="login" /> Giriş Yap</>)}
          </button>

          {isLocked && (
            <button
              type="button"
              onClick={handleSwitchAccount}
              className="w-full text-center text-xs font-semibold text-primary hover:underline transition py-1"
            >
              Başka Hesapla Giriş Yap
            </button>
          )}
        </div>

        {/* Legal Footer Links */}
        <div className="pt-4 border-t border-outline-variant/30 text-center space-y-2">
          <p className="text-[11px] text-on-surface-variant">
            Giriş yaparak platform şartlarımızı ve politikalarımızı kabul etmiş olursunuz.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-medium text-primary">
            <button
              type="button"
              onClick={() => openLegal("terms")}
              className="hover:underline transition cursor-pointer"
            >
              Kullanım Koşulları
            </button>
            <span className="text-outline-variant">•</span>
            <button
              type="button"
              onClick={() => openLegal("kvkk")}
              className="hover:underline transition cursor-pointer"
            >
              KVKK Metni
            </button>
            <span className="text-outline-variant">•</span>
            <button
              type="button"
              onClick={() => openLegal("cookies")}
              className="hover:underline transition cursor-pointer"
            >
              Çerez Politikası
            </button>
            <span className="text-outline-variant">•</span>
            <button
              type="button"
              onClick={() => openLegal("agreement")}
              className="hover:underline transition cursor-pointer"
            >
              Hizmet Sözleşmesi
            </button>
          </div>
        </div>
      </form>

      {/* Legal Document Modal & Cookie Consent */}
      <LegalDocumentModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        initialTab={legalModalTab}
      />
      <CookieConsentBanner onOpenPolicy={() => openLegal("cookies")} />
    </div>
  );
}
